import { useState } from 'react';
import { api, ApiError, FF_ENV, type Me } from '../api';
import { Banner } from '../components/Banner';

/**
 * Dev-mode sign-in.
 *
 * In a prod build this component renders an explanation instead of a form. The
 * gate is on the build-time constant, so the dev fields, the header names and
 * the login call are all absent from the prod bundle — not merely hidden.
 */
export function Login({ onSignedIn }: { onSignedIn: (me: Me) => void }) {
  const [email, setEmail] = useState('');
  const [secret, setSecret] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (FF_ENV === 'prod') {
    return (
      <Shell>
        <div className="card">
          <div className="card-body">
            <h2>Sign in</h2>
            <Banner tone="info">
              Google sign-in is not yet available. The production app is deployed
              but cannot be signed into until it ships.
            </Banner>
            <p className="small muted">
              Until then, the Close Agent is exercised on the dev environment.
            </p>
          </div>
        </div>
      </Shell>
    );
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      onSignedIn(await api.login(email.trim(), secret));
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.code === 'UNAUTHENTICATED'
            ? 'That email or secret was not accepted.'
            : err.code === 'FORBIDDEN'
              ? 'Signed in, but this account is not a member of any firm.'
              : err.message
          : 'Something went wrong.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Shell>
      <form className="card" onSubmit={submit}>
        <div className="card-body">
          <h2>Sign in</h2>
          <p className="small muted" style={{ marginBottom: '1.4rem' }}>
            Development environment. Google sign-in replaces this.
          </p>

          {error && <Banner tone="error">{error}</Banner>}

          <div className="field">
            <label htmlFor="email">Email</label>
            <input
              id="email" type="email" autoComplete="username" required
              placeholder="you@fiscalfounders.com"
              value={email} onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="secret">Dev shared secret</label>
            <input
              id="secret" type="password" autoComplete="current-password" required
              value={secret} onChange={(e) => setSecret(e.target.value)}
            />
            <p className="hint">
              <code>/ff/dev/auth/dev-shared-secret</code> in SSM Parameter Store.
            </p>
          </div>

          <button type="submit" className="block" disabled={busy} style={{ marginTop: '1.5rem' }}>
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </div>
      </form>
    </Shell>
  );
}

/** Centres the sign-in card and puts the mark above it, so the page reads as a
 *  front door rather than a form that happens to be on a dashboard. */
function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ maxWidth: '25rem', margin: '3.5rem auto 0' }}>
      <div style={{ textAlign: 'center', marginBottom: '1.6rem' }}>
        <img src="/logo.svg" alt="" width={40} height={40} />
        <h1 style={{ fontSize: '1.9rem', marginTop: '.5rem' }}>Close Agent</h1>
        <p className="muted small" style={{ margin: 0 }}>Fiscal Founders</p>
      </div>
      {children}
    </div>
  );
}
